import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMuseum } from '@/features/museum/context/MuseumContext';
import { FIRESTORE_COLLECTIONS } from '@/shared/constants';
import { Exhibition } from '@/shared/types';
import plusIcon from '@/assets/svg/plus_icon.svg';
import { useOrderManager } from '@/shared/hooks/useOrderManager';
import { reorganizeAfterDrag } from '@/shared/utils/orderManager';
import { useNotification } from '@/shared/context/NotificationContext';
import { useExhibitions } from '@/features/exhibitions/hooks/useExhibitions';
import { AsyncEmpty, AsyncError, AsyncSkeleton } from '@/shared/components/AsyncState';
import { ExhibitionCard } from '../components/ExhibitionCard';

export const ExhibitionsPage: React.FC = () => {
  const { t } = useTranslation();
  const { museum } = useMuseum();
  const navigate = useNavigate();
  const { showError } = useNotification();
  const {
    exhibitions: fetchedExhibitions,
    loading,
    error,
    refetch,
  } = useExhibitions();
  const [exhibitions, setExhibitions] = useState<Exhibition[]>([]);
  const [draggedExhibitionId, setDraggedExhibitionId] = useState<string | null>(null);
  const [dragOverExhibitionId, setDragOverExhibitionId] = useState<string | null>(null);
  const [dragOverPlace, setDragOverPlace] = useState<'before' | 'after'>('before');

  useEffect(() => {
    setExhibitions(fetchedExhibitions);
  }, [fetchedExhibitions]);

  const { reorderAfterDrag } = useOrderManager<Exhibition>({
    museumId: museum?.id || '',
    collectionPath: [FIRESTORE_COLLECTIONS.EXHIBITIONS],
    onSuccess: () => {
      refetch();
    },
    onError: (err) => {
      showError(err);
    },
  });

  const handleCreateExhibition = () => {
    navigate('exhibitions/new');
  };

  const handleDragStart = (exhibitionId: string) => {
    setDraggedExhibitionId(exhibitionId);
  };

  const handleDragOver = (e: React.DragEvent, exhibitionId: string, place: 'before' | 'after') => {
    e.preventDefault();
    if (draggedExhibitionId && draggedExhibitionId !== exhibitionId) {
      setDragOverExhibitionId(exhibitionId);
      setDragOverPlace(place);
    }
  };

  const handleDragLeave = () => {
    setDragOverExhibitionId(null);
  };

  const handleDrop = async (
    e: React.DragEvent,
    targetExhibitionId: string,
    place: 'before' | 'after'
  ) => {
    e.preventDefault();
    if (!draggedExhibitionId || draggedExhibitionId === targetExhibitionId || !museum?.id) {
      setDraggedExhibitionId(null);
      setDragOverExhibitionId(null);
      return;
    }

    const newOrders = reorganizeAfterDrag(
      exhibitions,
      draggedExhibitionId,
      targetExhibitionId,
      place === 'after'
    );
    const reordered = [...exhibitions]
      .sort((a, b) => (newOrders.get(a.id) ?? 0) - (newOrders.get(b.id) ?? 0))
      .map((item, index) => ({ ...item, order: index + 1 }));
    setExhibitions(reordered);

    setDraggedExhibitionId(null);
    setDragOverExhibitionId(null);

    const success = await reorderAfterDrag(
      exhibitions,
      draggedExhibitionId,
      targetExhibitionId,
      place === 'after',
      { skipSuccessCallback: true }
    );
    if (!success) {
      refetch();
    }
  };

  const handleDragEnd = () => {
    setDraggedExhibitionId(null);
    setDragOverExhibitionId(null);
  };

  return (
    <div className="exhibitions-page">
      <h1 className="page-title">{t('exhibitions.pageTitle')}</h1>
      <div className="exhibitions-grid">
        <button className="exhibition-card create-card" onClick={handleCreateExhibition}>
          <img src={plusIcon} alt="" className="plus-icon" />
          <span className="create-text">{t('exhibitions.createNewSeries')}</span>
        </button>
        {loading ? (
          <AsyncSkeleton variant="cards" count={3} />
        ) : error ? (
          <AsyncError message={error} onRetry={refetch} />
        ) : exhibitions.length === 0 ? (
          <AsyncEmpty
            title={t('exhibitions.noSeries')}
            message={t('exhibitions.noSeriesHint')}
            actionLabel={t('exhibitions.createNewSeries')}
            onAction={handleCreateExhibition}
          />
        ) : (
          exhibitions.map((exhibition: Exhibition) => {
            const insertionLine =
              dragOverExhibitionId === exhibition.id ? dragOverPlace : null;
            return (
              <div key={exhibition.id} className="exhibition-card-cell">
                {insertionLine === 'before' && (
                  <div className="drag-insertion-line-vertical insertion-before" aria-hidden />
                )}
                <ExhibitionCard
                  exhibition={exhibition}
                  isDragging={draggedExhibitionId === exhibition.id}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onDragEnd={handleDragEnd}
                />
                {insertionLine === 'after' && (
                  <div className="drag-insertion-line-vertical insertion-after" aria-hidden />
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
